// apps/web/src/components/RewardDebugPanel/RewardDebugPanel.tsx
// ─────────────────────────────────────────────────────────────────────────
// DEV-ONLY reward debugger. Renders nothing in production (guarded by
// import.meta.env.DEV). Lets you watch XP/level state live, inspect the
// idempotency ledger, and fire any reward event with one click so you don't
// have to walk the whole app to test each path.
//
// Remove the mount in AppLayout before launch (or leave it — it self-hides
// in prod builds).
// ─────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	selectTotalXp,
	selectLevel,
	selectLevelLabel,
	selectXpIntoLevel,
	selectXpForNextLevel,
	selectXpProgressPct,
} from '../../store/xpSlice';
import { selectGemBalance } from '../../store/economySlice';
import { processRewardEvent } from '../../lib/rewards';
import type { AppDispatch } from '../../store';
import type {
	RewardEvent,
	RewardEventType,
	FlashcardRating,
} from '@nisk/core/rewards';

const LEDGER_KEY = 'nis.rewards.ledger';
const XP_KEY = 'nis.gamification.xp';

function readLedger(): string[] {
	try {
		const raw = localStorage.getItem(LEDGER_KEY);
		return raw ? (JSON.parse(raw) as string[]) : [];
	} catch {
		return [];
	}
}

// One-click test buttons. Each builds a full RewardEvent with a UNIQUE id
// (timestamped) so the ledger never blocks repeated manual testing — except
// the explicitly-idempotent ones, which reuse a fixed id so you can watch
// dedup work.
interface TestButton {
	label: string;
	build: () => RewardEvent;
}

function ev(
	type: RewardEventType,
	extra: Partial<RewardEvent> = {},
): RewardEvent {
	return {
		id: `${type}:${Date.now()}:${Math.random().toString(36).slice(2, 6)}`,
		type,
		occurredAt: new Date().toISOString(),
		...extra,
	};
}

// Fixed-id events to demonstrate idempotency (fire twice → second is a no-op).
function fixedEv(
	type: RewardEventType,
	fixedId: string,
	extra: Partial<RewardEvent> = {},
): RewardEvent {
	return {
		id: fixedId,
		type,
		occurredAt: new Date().toISOString(),
		...extra,
	};
}

const TEST_BUTTONS: TestButton[] = [
	{ label: 'lesson_read (+10)', build: () => ev('lesson_read') },
	{
		label: 'lesson_read FIXED id (dedup test)',
		build: () => fixedEv('lesson_read', 'lesson_read:DEBUG_FIXED'),
	},
	{
		label: 'quiz_passed (+20, +5💎)',
		build: () => ev('quiz_passed', { moduleId: 'dbg', assessmentId: 'q1' }),
	},
	{
		label: 'quiz_first_try (+30, +20💎)',
		build: () =>
			ev('quiz_first_try_passed', {
				moduleId: 'dbg',
				assessmentId: 'q1',
			}),
	},
	{
		label: 'quiz_aced (+5, +10💎)',
		build: () =>
			ev('quiz_aced', {
				moduleId: 'dbg',
				assessmentId: 'q1',
				scorePercent: 100,
			}),
	},
	{
		label: 'final_test_passed (+50, +100💎)',
		build: () => ev('final_test_passed', { moduleId: 'dbg' }),
	},
	{
		label: 'module_mastered (+100, +50💎)',
		build: () => ev('module_mastered', { moduleId: 'dbg' }),
	},
	{
		label: 'flashcard good (+3)',
		build: () =>
			ev('flashcard_reviewed', {
				cardId: 'dbg',
				rating: 'good' as FlashcardRating,
			}),
	},
	{
		label: 'flashcards_all_due_cleared (+15)',
		build: () => ev('flashcards_all_due_cleared'),
	},
	{
		label: 'daily_review_cleared (+5💎, no XP)',
		build: () => ev('daily_review_cleared'),
	},
	{
		label: 'lesson_flashcards_checkpoint (+15)',
		build: () => ev('lesson_flashcards_checkpoint', { lessonId: 'dbg' }),
	},
	{
		label: 'discovery_quiz_completed (+10💎, no XP)',
		build: () => ev('discovery_quiz_completed', { quizId: 'dbg' }),
	},
	{
		label: 'scenario_first_saved FIXED (+10💎, once ever)',
		build: () =>
			fixedEv('scenario_first_saved', 'scenario_first_saved:once'),
	},
	{
		label: 'market_replay_first FIXED (+10💎, once ever)',
		build: () =>
			fixedEv('market_replay_first', 'market_replay_first:once'),
	},
	{
		label: 'school_exhibit_completed (+10💎, no XP)',
		build: () => ev('school_exhibit_completed', { schoolId: 'dbg' }),
	},
	{
		label: 'playlist_completed (+25, +10💎) [dormant]',
		build: () => ev('playlist_completed', { playlistId: 'dbg' }),
	},
	{
		label: 'daily_login (+5💎, no XP)',
		build: () => ev('daily_login'),
	},
	{
		label: 'streak_milestone 7d (+25💎, no XP)',
		build: () => ev('streak_milestone', { streakDays: 7 }),
	},
	{
		label: '⚡ +100 XP (force level-up test)',
		build: () => ev('module_mastered', { moduleId: `dbg-${Date.now()}` }),
	},
];

const RewardDebugPanel: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const [open, setOpen] = useState(false);
	const [log, setLog] = useState<string[]>([]);

	const totalXp = useSelector(selectTotalXp);
	const level = useSelector(selectLevel);
	const label = useSelector(selectLevelLabel);
	const xpInto = useSelector(selectXpIntoLevel);
	const xpForNext = useSelector(selectXpForNextLevel);
	const pct = useSelector(selectXpProgressPct);
	const gems = useSelector(selectGemBalance);

	if (!import.meta.env.DEV) return null;

	const fire = async (btn: TestButton) => {
		const event = btn.build();
		const res = await dispatch(processRewardEvent(event));
		const line = `${event.type} → xp+${res.xpAwarded} gem+${res.gemsAwarded}${
			res.leveledUp ? ' ⬆LVL' : ''
		}${res.skipped ? ' [SKIPPED/dedup]' : ''}`;
		setLog((l) => [line, ...l].slice(0, 12));
	};

	const resetXp = () => {
		localStorage.setItem(XP_KEY, '0');
		localStorage.removeItem(LEDGER_KEY);
		setLog((l) => [
			'*** reset XP + ledger. RELOAD to rehydrate ***',
			...l,
		]);
	};

	const ledger = readLedger();

	const wrap: React.CSSProperties = {
		position: 'fixed',
		bottom: 16,
		left: 16,
		zIndex: 99999,
		fontFamily: 'monospace',
		fontSize: 11,
	};
	const btnStyle: React.CSSProperties = {
		background: '#c8ff00',
		color: '#0d0d0d',
		border: 'none',
		borderRadius: 6,
		padding: '6px 10px',
		fontWeight: 700,
		cursor: 'pointer',
		fontFamily: 'monospace',
		fontSize: 11,
	};
	const panelStyle: React.CSSProperties = {
		marginTop: 8,
		width: 320,
		maxHeight: '70vh',
		overflowY: 'auto',
		background: '#0d0d0d',
		color: '#fff',
		border: '1px solid #c8ff00',
		borderRadius: 10,
		padding: 12,
		boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
	};
	const evBtn: React.CSSProperties = {
		display: 'block',
		width: '100%',
		textAlign: 'left',
		background: 'rgba(255,255,255,0.06)',
		color: '#c8ff00',
		border: '1px solid rgba(200,255,0,0.2)',
		borderRadius: 5,
		padding: '5px 8px',
		margin: '3px 0',
		cursor: 'pointer',
		fontFamily: 'monospace',
		fontSize: 10.5,
	};

	return (
		<div style={wrap}>
			<button
				style={btnStyle}
				onClick={() => setOpen((o) => !o)}>
				{open ? '✕ DEBUG' : '🐛 REWARDS'}
			</button>

			{open && (
				<div style={panelStyle}>
					{/* Live state */}
					<div style={{ marginBottom: 10 }}>
						<div style={{ color: '#c8ff00', fontWeight: 700 }}>
							STATE
						</div>
						<div>
							Lv {level} · {label}
						</div>
						<div>Total XP: {totalXp}</div>
						<div>
							This level: {xpInto}/{xpForNext} ({pct}%)
						</div>
						<div style={{ marginTop: 4 }}>Gems: {gems}</div>
					</div>

					{/* Fire events */}
					<div style={{ color: '#c8ff00', fontWeight: 700 }}>
						FIRE EVENT
					</div>
					{TEST_BUTTONS.map((b) => (
						<button
							key={b.label}
							style={evBtn}
							onClick={() => fire(b)}>
							{b.label}
						</button>
					))}

					<button
						style={{
							...evBtn,
							color: '#ff6b6b',
							borderColor: 'rgba(255,107,107,0.4)',
						}}
						onClick={resetXp}>
						⟲ reset XP + ledger (then reload)
					</button>

					{/* Result log */}
					<div
						style={{
							color: '#c8ff00',
							fontWeight: 700,
							marginTop: 10,
						}}>
						RESULT LOG
					</div>
					{log.length === 0 && (
						<div style={{ opacity: 0.5 }}>(no events yet)</div>
					)}
					{log.map((l, i) => (
						<div
							key={i}
							style={{
								borderBottom:
									'1px solid rgba(255,255,255,0.08)',
								padding: '2px 0',
								color: l.includes('SKIPPED')
									? '#ff6b6b'
									: '#9be870',
							}}>
							{l}
						</div>
					))}

					{/* Ledger */}
					<div
						style={{
							color: '#c8ff00',
							fontWeight: 700,
							marginTop: 10,
						}}>
						LEDGER ({ledger.length})
					</div>
					{ledger
						.slice(-10)
						.reverse()
						.map((k, i) => (
							<div
								key={i}
								style={{
									opacity: 0.7,
									fontSize: 10,
									wordBreak: 'break-all',
								}}>
								{k}
							</div>
						))}
				</div>
			)}
		</div>
	);
};

export default RewardDebugPanel;
